import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { minutesAPI, meetingAPI, bodyAPI, aiAPI, organizationAPI } from '../api';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import { format, parseISO } from 'date-fns';
import { 
  Plus, Search, Filter, FileText, Loader2, Calendar,
  Clock, Users, ChevronRight, Sparkles, CheckCircle, 
  AlertCircle, Edit, Eye, MoreVertical, Send
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { toast } from 'sonner';

const Minutes = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  const [minutes, setMinutes] = useState([]);
  const [meetings, setMeetings] = useState([]);
  const [bodies, setBodies] = useState([]);
  const [organization, setOrganization] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [selectedMeeting, setSelectedMeeting] = useState(null);
  const [creating, setCreating] = useState(false);
  const [generatingAI, setGeneratingAI] = useState(false);

  useEffect(() => {
    fetchData();
  }, [statusFilter]);

  const fetchData = async () => {
    try {
      const params = {};
      if (statusFilter !== 'all') params.status = statusFilter;

      const [minutesRes, meetingsRes, bodiesRes, orgRes] = await Promise.all([
        minutesAPI.getAll(params),
        meetingAPI.getAll({ status: 'completed' }),
        bodyAPI.getAll(),
        user?.organization_id ? organizationAPI.get(user.organization_id) : Promise.resolve({ data: null }),
      ]);

      setMinutes(minutesRes.data);
      setMeetings(meetingsRes.data);
      setBodies(bodiesRes.data);
      setOrganization(orgRes.data);
    } catch (err) {
      console.error('Failed to fetch data:', err);
    } finally {
      setLoading(false);
    }
  };

  const getBodyName = (bodyId) => {
    const body = bodies.find(b => b.id === bodyId);
    return body?.name || 'Unknown';
  };

  const getMeetingForMinute = (meetingId) => {
    return meetings.find(m => m.id === meetingId);
  };

  const getStatusColor = (status) => {
    const colors = {
      draft: 'bg-amber-100 text-amber-800 border-amber-300',
      under_review: 'bg-blue-100 text-blue-800 border-blue-300',
      for_approval: 'bg-cyan-100 text-cyan-800 border-cyan-300',
      approved: 'bg-green-100 text-green-800 border-green-300',
      rejected: 'bg-red-100 text-red-800 border-red-300',
    };
    return colors[status] || 'bg-gray-100 text-gray-700 border-gray-300';
  };

  const createMinutes = async (useAI = false) => {
    if (!selectedMeeting) return;
    
    setCreating(true);
    if (useAI) setGeneratingAI(true);
    
    try {
      // Create minutes record
      const response = await minutesAPI.create({
        organization_id: user.organization_id,
        meeting_id: selectedMeeting.id,
        title: `Minutes - ${selectedMeeting.title}`,
      });
      
      if (useAI && organization?.ai_enabled) {
        try {
          const aiResponse = await aiAPI.draftMinutes(selectedMeeting.id);
          await minutesAPI.update(response.data.id, {
            sections: [{
              id: 'main',
              title: 'Minutes',
              content: aiResponse.data.draft,
              order: 0
            }],
            ai_generated: true
          });
          toast.success('AI-drafted minutes created');
        } catch (aiErr) {
          toast.error('AI generation failed, created blank minutes');
        }
      } else {
        toast.success('Minutes draft created');
      }
      
      setCreateDialogOpen(false);
      setSelectedMeeting(null);
      fetchData();
      navigate(`/minutes/${response.data.id}/edit`);
    } catch (err) {
      toast.error('Failed to create minutes');
    } finally {
      setCreating(false);
      setGeneratingAI(false);
    }
  };

  const updateMinuteStatus = async (minuteId, newStatus) => {
    try {
      await minutesAPI.update(minuteId, { status: newStatus });
      setMinutes(minutes.map(m => 
        m.id === minuteId ? { ...m, status: newStatus } : m
      ));
      toast.success(`Minutes ${newStatus.replace('_', ' ')}`);
    } catch (err) {
      toast.error('Failed to update status');
    }
  };

  const filteredMinutes = minutes.filter(minute =>
    minute.title.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Check which meetings already have minutes
  const meetingsWithMinutes = new Set(minutes.map(m => m.meeting_id));
  const availableMeetings = meetings.filter(m => !meetingsWithMinutes.has(m.id));

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="h-8 w-8 animate-spin text-[#00A3A3]" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="minutes-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-[#1A1D21]">Minutes</h1>
          <p className="text-[#5A6570] mt-1">Draft, review, and approve meeting minutes</p>
        </div>
        <Button 
          onClick={() => setCreateDialogOpen(true)}
          className="bg-[#00A3A3] hover:bg-[#008585]"
          data-testid="create-minutes-btn"
        >
          <Plus className="h-4 w-4 mr-2" />
          Draft Minutes
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-col md:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-[#5A6570]" />
          <Input
            placeholder="Search minutes..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 border-[#E2E6EA]"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-full md:w-[180px] border-[#E2E6EA]">
            <Filter className="h-4 w-4 mr-2 text-[#5A6570]" />
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="draft">Draft</SelectItem>
            <SelectItem value="under_review">Under Review</SelectItem>
            <SelectItem value="for_approval">For Approval</SelectItem>
            <SelectItem value="approved">Approved</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Workflow Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="border-[#E2E6EA]">
          <CardContent className="p-4">
            <p className="text-sm text-[#5A6570]">Drafts</p>
            <p className="text-2xl font-bold text-amber-600">
              {minutes.filter(m => m.status === 'draft').length}
            </p>
          </CardContent>
        </Card>
        <Card className="border-[#E2E6EA]">
          <CardContent className="p-4">
            <p className="text-sm text-[#5A6570]">Under Review</p>
            <p className="text-2xl font-bold text-blue-600">
              {minutes.filter(m => m.status === 'under_review').length}
            </p>
          </CardContent>
        </Card>
        <Card className="border-[#E2E6EA]">
          <CardContent className="p-4">
            <p className="text-sm text-[#5A6570]">For Approval</p>
            <p className="text-2xl font-bold text-cyan-600">
              {minutes.filter(m => m.status === 'for_approval').length}
            </p>
          </CardContent>
        </Card>
        <Card className="border-[#E2E6EA]">
          <CardContent className="p-4">
            <p className="text-sm text-[#5A6570]">Approved</p>
            <p className="text-2xl font-bold text-green-600">
              {minutes.filter(m => m.status === 'approved').length}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Minutes Should Include */}
      <div className="p-4 bg-[#00A3A3]/5 border border-[#00A3A3]/20 rounded-lg">
        <h4 className="font-medium text-[#1A1D21] mb-2">Minutes Should Include:</h4>
        <ul className="text-sm text-[#5A6570] space-y-1 grid md:grid-cols-2 gap-x-4">
          <li className="flex items-center gap-2"><CheckCircle className="h-3 w-3 text-[#00A3A3]" />Date, time, and place of meeting</li>
          <li className="flex items-center gap-2"><CheckCircle className="h-3 w-3 text-[#00A3A3]" />Names of attendees and absentees</li>
          <li className="flex items-center gap-2"><CheckCircle className="h-3 w-3 text-[#00A3A3]" />Approval of previous minutes</li>
          <li className="flex items-center gap-2"><CheckCircle className="h-3 w-3 text-[#00A3A3]" />Reports presented</li>
          <li className="flex items-center gap-2"><CheckCircle className="h-3 w-3 text-[#00A3A3]" />Resolutions passed</li>
          <li className="flex items-center gap-2"><CheckCircle className="h-3 w-3 text-[#00A3A3]" />Action items with responsible persons</li>
          <li className="flex items-center gap-2"><CheckCircle className="h-3 w-3 text-[#00A3A3]" />Next meeting schedule</li>
        </ul>
      </div>

      {/* Minutes List */}
      {filteredMinutes.length === 0 ? (
        <Card className="border-[#E2E6EA]">
          <CardContent className="py-16 text-center">
            <FileText className="h-12 w-12 mx-auto mb-4 text-[#A8B1B9]" />
            <h3 className="text-lg font-medium text-[#1A1D21] mb-2">No minutes found</h3>
            <p className="text-[#5A6570] mb-4">
              {searchTerm ? 'Try adjusting your search' : 'Create minutes from a completed meeting'}
            </p>
            <Button 
              onClick={() => setCreateDialogOpen(true)}
              className="bg-[#00A3A3] hover:bg-[#008585]"
            >
              <Plus className="h-4 w-4 mr-2" />
              Draft Minutes
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {filteredMinutes.map((minute) => {
            const meeting = getMeetingForMinute(minute.meeting_id);
            return (
              <Card 
                key={minute.id} 
                className="border-[#E2E6EA] hover:border-[#00A3A3] transition-colors cursor-pointer"
                onClick={() => navigate(`/minutes/${minute.id}/edit`)}
              >
                <CardContent className="p-5">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex items-start gap-4 flex-1 min-w-0">
                      <div className="p-2 bg-[#00A3A3]/10 rounded-lg">
                        <FileText className="h-6 w-6 text-[#00A3A3]" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-3 mb-1 flex-wrap">
                          <h3 className="font-semibold text-[#1A1D21]">{minute.title}</h3>
                          <span className={`text-xs px-2 py-0.5 rounded border ${getStatusColor(minute.status)}`}>
                            {minute.status.replace('_', ' ')}
                          </span>
                          {minute.ai_generated && (
                            <span className="text-xs px-2 py-0.5 rounded bg-purple-100 text-purple-800 flex items-center gap-1">
                              <Sparkles className="h-3 w-3" />
                              AI Draft
                            </span>
                          )}
                        </div>
                        
                        <div className="flex flex-wrap items-center gap-4 text-sm text-[#5A6570]">
                          {meeting && (
                            <>
                              <span className="flex items-center gap-1">
                                <Calendar className="h-3.5 w-3.5" />
                                {format(parseISO(meeting.date_time), 'MMM d, yyyy')}
                              </span>
                              <span>{getBodyName(meeting.body_id)}</span>
                            </>
                          )}
                          <span className="flex items-center gap-1">
                            <Clock className="h-3.5 w-3.5" />
                            Updated {format(parseISO(minute.updated_at), 'MMM d, yyyy')}
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center gap-2">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                          <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => navigate(`/minutes/${minute.id}/edit`)}>
                            <Edit className="h-4 w-4 mr-2" />
                            Edit
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => navigate(`/minutes/${minute.id}`)}>
                            <Eye className="h-4 w-4 mr-2" />
                            View
                          </DropdownMenuItem>
                          {minute.status === 'draft' && (
                            <DropdownMenuItem onClick={(e) => {
                              e.stopPropagation();
                              updateMinuteStatus(minute.id, 'under_review');
                            }}>
                              <Send className="h-4 w-4 mr-2" />
                              Submit for Review
                            </DropdownMenuItem>
                          )}
                          {minute.status === 'under_review' && (
                            <DropdownMenuItem onClick={(e) => {
                              e.stopPropagation();
                              updateMinuteStatus(minute.id, 'for_approval');
                            }}>
                              <CheckCircle className="h-4 w-4 mr-2" />
                              Ready for Approval
                            </DropdownMenuItem>
                          )}
                          {minute.status === 'for_approval' && (
                            <DropdownMenuItem onClick={(e) => {
                              e.stopPropagation();
                              updateMinuteStatus(minute.id, 'approved');
                            }}>
                              <CheckCircle className="h-4 w-4 mr-2 text-green-600" />
                              Approve
                            </DropdownMenuItem>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                      <ChevronRight className="h-5 w-5 text-[#A8B1B9]" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Create Minutes Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Draft Meeting Minutes</DialogTitle>
            <DialogDescription>
              Select a completed meeting to create minutes
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {availableMeetings.length === 0 ? (
              <div className="text-center py-4 text-[#5A6570]">
                <Calendar className="h-10 w-10 mx-auto mb-3 text-[#A8B1B9]" />
                <p>No meetings available for minutes creation</p>
                <p className="text-sm mt-1">Complete a meeting first or all meetings already have minutes</p>
              </div>
            ) : (
              <div className="space-y-2 max-h-[300px] overflow-y-auto">
                {availableMeetings.map((meeting) => (
                  <button
                    key={meeting.id}
                    onClick={() => setSelectedMeeting(meeting)}
                    className={`w-full text-left p-4 rounded-lg border transition-colors ${
                      selectedMeeting?.id === meeting.id
                        ? 'border-[#00A3A3] bg-[#00A3A3]/5'
                        : 'border-[#E2E6EA] hover:border-[#00A3A3]'
                    }`}
                  >
                    <p className="font-medium text-[#1A1D21]">{meeting.title}</p>
                    <div className="flex items-center gap-3 mt-1 text-sm text-[#5A6570]">
                      <span>{format(parseISO(meeting.date_time), 'MMM d, yyyy')}</span>
                      <span>{getBodyName(meeting.body_id)}</span>
                    </div>
                  </button>
                ))}
              </div>
            )}

            {organization?.ai_enabled && selectedMeeting && (
              <div className="flex items-center gap-3 p-4 bg-[#00A3A3]/5 border border-[#00A3A3]/20 rounded-lg">
                <Sparkles className="h-5 w-5 text-[#00A3A3]" />
                <div className="flex-1">
                  <p className="font-medium text-[#1A1D21]">AI-Assisted Drafting</p>
                  <p className="text-xs text-[#5A6570]">Let Claude AI generate an initial draft from meeting notes</p>
                </div>
              </div>
            )}
          </div>
          <DialogFooter className="flex gap-2">
            <Button variant="outline" onClick={() => setCreateDialogOpen(false)} className="border-[#E2E6EA]">
              Cancel
            </Button>
            <Button
              onClick={() => createMinutes(false)}
              disabled={!selectedMeeting || creating}
              variant="outline"
              className="border-[#E2E6EA]"
            >
              {creating && !generatingAI ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
              Create Blank
            </Button>
            {organization?.ai_enabled && (
              <Button
                onClick={() => createMinutes(true)}
                disabled={!selectedMeeting || creating}
                className="bg-[#00A3A3] hover:bg-[#008585]"
              >
                {generatingAI ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <Sparkles className="h-4 w-4 mr-2" />
                    Generate with AI
                  </>
                )}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Minutes;
